import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { ChevronDown, ChevronUp, Eye, X, ArrowUp, Copy, Check } from "lucide-react";
import { toast } from "sonner";

// Category-specific background images and colors
const CATEGORY_BACKGROUNDS = {
  events: {
    defaultBg: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "#667eea"
  },
  instructor: {
    defaultBg: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
    color: "#4facfe"
  },
  culinary: {
    defaultBg: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
    color: "#f093fb"
  },
  makeup: {
    defaultBg: "linear-gradient(135deg, #ff0844 0%, #ffb199 100%)",
    color: "#ff0844"
  },
  investor: {
    defaultBg: "linear-gradient(135deg, #0c0c0c 0%, #434343 100%)",
    color: "#0c0c0c"
  },
  hair: {
    defaultBg: "linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)",
    color: "#3b82f6"
  },
  nails: {
    defaultBg: "linear-gradient(135deg, #ec4899 0%, #a855f7 100%)",
    color: "#ec4899"
  },
  default: {
    defaultBg: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "#667eea"
  }
};

// PreferenceCardModal - Shows user's preference card in a modal
export const PreferenceCardModal = ({ 
  open, 
  onClose, 
  user, 
  category = "default",
  eventData = null,
  overrideBackground = null,
  overrideLogo = null,
  roleTags = [],
  onCantMakeIt = null
}) => {
  const [showDetails, setShowDetails] = useState(false);
  const [copied, setCopied] = useState(false);
  
  const categoryConfig = CATEGORY_BACKGROUNDS[category] || CATEGORY_BACKGROUNDS.default;
  const backgroundImage = overrideBackground || null;
  const backgroundStyle = backgroundImage 
    ? { backgroundImage: `url(${backgroundImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }
    : { background: categoryConfig.defaultBg };
  
  // Gather user preferences/details to show
  const userDetails = {
    dietary: user?.defaults?.dietary || user?.dietary_restrictions || [],
    accessibility: user?.defaults?.accessibility || user?.accessibility_needs || [],
    interests: user?.defaults?.interests || user?.interests || [],
    vocalTics: user?.vocal_tics || user?.invisible_disabilities || null,
    // Additional intake/preference data
    skinType: user?.skin_type,
    allergies: user?.allergies || [],
    preferredLook: user?.preferred_look,
    budgetRange: user?.budget_range,
    // Inspo and custom data
    inspirations: user?.inspiration_images || [],
  };
  
  const handleCopyLink = async () => {
    const shareUrl = `${window.location.origin}/me/${user?.wallet_id || user?.user_id}/preferences`;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error("Failed to copy link");
    }
  };
  
  const userName = user?.first_name && user?.last_name 
    ? `${user.first_name} ${user.last_name}`
    : user?.name || "Guest";
    
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-sm p-0 overflow-hidden rounded-2xl border-0">
        {/* Card Header with Background */}
        <div 
          className="relative h-48 flex flex-col justify-between p-4"
          style={backgroundStyle}
          data-testid="preference-card-modal"
        >
          {/* Overlay for better text readability */}
          <div className="absolute inset-0 bg-black/20" />
          
          {/* Top row - Logo/Category Icon + Close */}
          <div className="relative z-10 flex items-start justify-between">
            {/* Logo or Category Icon */}
            <div className="w-10 h-10 rounded-lg bg-white/20 backdrop-blur-sm flex items-center justify-center overflow-hidden">
              {overrideLogo ? (
                <img src={overrideLogo} alt="" className="w-8 h-8 object-contain" />
              ) : (
                <span className="text-white text-lg font-bold">
                  {category.charAt(0).toUpperCase()}
                </span>
              )}
            </div>
            
            {/* Close button */}
            <Button 
              variant="ghost" 
              size="icon" 
              className="text-white hover:bg-white/20 h-8 w-8"
              onClick={() => onClose(false)}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
          
          {/* Center - User Photo */}
          <div className="relative z-10 flex justify-center -mb-10">
            <div className="w-20 h-20 rounded-full border-4 border-white shadow-lg overflow-hidden bg-white">
              {user?.profile_photo_url ? (
                <img 
                  src={user.profile_photo_url} 
                  alt={userName} 
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                  <span className="text-2xl font-bold text-gray-500">
                    {userName.charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Card Body */}
        <div className="bg-white pt-12 pb-4 px-4">
          {/* User Name */}
          <div className="text-center mb-3">
            <h2 className="text-lg font-semibold text-gray-900">{userName}</h2>
            
            {/* Role Tags */}
            {roleTags.length > 0 && (
              <div className="flex flex-wrap justify-center gap-1 mt-2">
                {roleTags.map((tag, idx) => (
                  <Badge 
                    key={idx} 
                    variant="outline" 
                    className="text-[10px] px-2 py-0.5"
                    style={{ borderColor: categoryConfig.color, color: categoryConfig.color }}
                  >
                    {tag}
                  </Badge>
                ))}
              </div>
            )}
          </div>
          
          {/* Vocal Tics / Accessibility Badge */}
          {userDetails.vocalTics && (
            <div className="mb-3 p-2 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-xs text-amber-700 font-medium">
                Accessibility Note: {userDetails.vocalTics}
              </p>
            </div>
          )}
          
          {/* "What the venue sees" label */}
          <p className="text-[10px] text-gray-400 text-center uppercase tracking-wider mb-2">
            What the venue sees
          </p>
          
          {/* Quick Preview Items */}
          <div className="space-y-2 mb-3">
            {userDetails.dietary?.length > 0 && (
              <div className="flex items-center gap-2 text-xs text-gray-600">
                <span className="font-medium text-gray-700">Dietary:</span>
                <span>{Array.isArray(userDetails.dietary) ? userDetails.dietary.join(", ") : userDetails.dietary}</span>
              </div>
            )}
            {userDetails.accessibility?.length > 0 && (
              <div className="flex items-center gap-2 text-xs text-gray-600">
                <span className="font-medium text-gray-700">Accessibility:</span>
                <span>{Array.isArray(userDetails.accessibility) ? userDetails.accessibility.join(", ") : userDetails.accessibility}</span>
              </div>
            )}
          </div>
          
          {/* See All Details Dropdown */}
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="w-full flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
            data-testid="see-all-details-btn"
          >
            <span className="text-sm font-medium text-gray-700">See All Details</span>
            {showDetails ? (
              <ChevronUp className="w-4 h-4 text-gray-500" />
            ) : (
              <ChevronDown className="w-4 h-4 text-gray-500" />
            )}
          </button>
          
          {/* Expanded Details */}
          {showDetails && (
            <div className="mt-3 p-3 bg-gray-50 rounded-lg space-y-3 animate-in slide-in-from-top-2 duration-200">
              {/* Interests */}
              {userDetails.interests?.length > 0 && (
                <div>
                  <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">Interests</p>
                  <div className="flex flex-wrap gap-1">
                    {(Array.isArray(userDetails.interests) ? userDetails.interests : [userDetails.interests]).map((interest, idx) => (
                      <Badge key={idx} variant="secondary" className="text-[10px]">
                        {interest}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Skin Type (for makeup) */}
              {userDetails.skinType && (
                <div>
                  <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">Skin Type</p>
                  <p className="text-xs text-gray-700">{userDetails.skinType}</p>
                </div>
              )}
              
              {/* Allergies */}
              {userDetails.allergies?.length > 0 && (
                <div>
                  <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">Allergies</p>
                  <p className="text-xs text-gray-700">{userDetails.allergies.join(", ")}</p>
                </div>
              )}
              
              {/* Preferred Look */}
              {userDetails.preferredLook && (
                <div>
                  <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">Preferred Look</p>
                  <p className="text-xs text-gray-700">{userDetails.preferredLook}</p>
                </div>
              )}
              
              {/* Budget Range */}
              {userDetails.budgetRange && (
                <div>
                  <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">Budget</p>
                  <p className="text-xs text-gray-700">{userDetails.budgetRange}</p>
                </div>
              )}
              
              {/* Inspirations */}
              {userDetails.inspirations?.length > 0 && (
                <div>
                  <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">Inspiration</p>
                  <div className="grid grid-cols-3 gap-1">
                    {userDetails.inspirations.slice(0, 6).map((img, idx) => (
                      <div key={idx} className="aspect-square rounded overflow-hidden bg-gray-200">
                        <img 
                          src={img.image_url || img.url || img} 
                          alt="" 
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Action Buttons */}
          <div className="flex gap-2 mt-4">
            <Button
              variant="outline"
              className="flex-1"
              onClick={handleCopyLink}
              data-testid="copy-share-link-btn"
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4 mr-1" />
                  Copied
                </>
              ) : (
                <>
                  <ArrowUp className="w-4 h-4 mr-1" />
                  Share
                </>
              )}
            </Button>
            
            {onCantMakeIt && (
              <Button
                variant="outline"
                className="flex-1 text-red-600 border-red-200 hover:bg-red-50"
                onClick={onCantMakeIt}
                data-testid="cant-make-it-btn"
              >
                Can't Make It
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Eyeball trigger button component for showing preference card
export const PreferenceCardTrigger = ({ onClick, size = "default" }) => {
  const sizeClasses = {
    sm: "w-6 h-6",
    default: "w-8 h-8",
    lg: "w-10 h-10"
  };
  
  return (
    <button
      onClick={onClick}
      className={`${sizeClasses[size]} rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm flex items-center justify-center transition-all shadow-sm hover:shadow-md`}
      data-testid="preference-card-trigger"
      title="View Preference Card"
    >
      <Eye className={size === "sm" ? "w-3.5 h-3.5" : "w-4 h-4"} />
    </button>
  );
};

export default PreferenceCardModal;
